import {
  Box,
  Button,
  Grid,
  Heading,
  HStack,
  IconButton,
  Image,
  Link,
  Text,
  Tooltip,
  useDisclosure,
  VStack
} from '@chakra-ui/react';

import React, { useState } from 'react';
import profileLogo from '@assets/icons/profile.svg';
import { getActiveDeviceDetailsList, getUserDetailsList, SUPPORTED_USER_DETAILS_UPDATE_FIELDS } from './utils';
import ActiveDevice from './ActiveDevice';
import { AiOutlinePlusCircle } from 'react-icons/ai';
import { BsCashCoin, BsPeopleFill } from 'react-icons/bs';
import ModalLayout from '@components/Modal/ModalLayout';
import AddFreeTransfers from '@components/Modal/AddFreeTransfers';
import RawDataViewer from '@components/Modal/RawDataViewer';
import CopyableText from '@components/CopyableText';
import { Link as ReactLink } from 'react-router-dom';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { BiAward, BiSync } from 'react-icons/bi';
import { GiTicket } from 'react-icons/gi';
import { MdEdit } from 'react-icons/md';
import { FaArrowRightFromBracket } from 'react-icons/fa6';
import { useDispatch } from 'react-redux';
import { getUserById, updateVdaEligibilityStatus } from '@store/actions/usersActions';
import { setAlert } from '@store/actions/alertActions';
import { updateUserDetails } from '@store/actions/cxOperationActions';
import useAuthRole from '@hooks/useAuthRole';
import { ALERT_STATUS_TYPE, ROLES } from '@constants';
import { TbGitBranch } from 'react-icons/tb';
import { formatDobDate } from '@utils/dateUtils';
import UserDetailsUpdateModal from '@components/UserDetailsUpdateModal';

const UserDetails = ({
  userData,
  allowAddFreeTransfer,
  referralCode,
  allowSyncWallet,
  onWalletSync,
  isSyncingWallet,
  onVanceCashWalletSync
}) => {
  const { hasAnyAuthority } = useAuthRole();
  const userDetailsList = getUserDetailsList(userData);
  const activeDeviceDetails = getActiveDeviceDetailsList(userData.activeDevice);
  const {
    isOpen: isFreeTransferModalOpen,
    onOpen: onFreeTransferModalOpen,
    onClose: onFreeTransferModalClose
  } = useDisclosure();
  const {
    isOpen: isEmailUpdateOpen,
    onOpen: onEmailUpdateOpen,
    onClose: onEmailUpdateClose
  } = useDisclosure();
  const {
    isOpen: isPhoneUpdateOpen,
    onOpen: onPhoneUpdateOpen,
    onClose: onPhoneUpdateClose
  } = useDisclosure();
  const {
    isOpen: isUsernameUpdateOpen,
    onOpen: onUsernameUpdateOpen,
    onClose: onUsernameUpdateClose
  } = useDisclosure();
  const {
    isOpen: isUpdateVdaStatusOpen,
    onOpen: onUpdateVdaStatusOpen,
    onClose: onUpdateVdaStatusClose
  } = useDisclosure();
  const {
    isOpen: isKycUpdateOpen,
    onOpen: onKycUpdateOpen,
    onClose: onKycUpdateClose
  } = useDisclosure();
  const {
    isOpen: isDobUpdateOpen,
    onOpen: onDobUpdateOpen,
    onClose: onDobUpdateClose
  } = useDisclosure();
  const {
    isOpen: isFeaturesModalOpen,
    onOpen: onFeaturesModalOpen,
    onClose: onFeaturesModalClose
  } = useDisclosure();

  const [versionNumber, setVersionNumber] = useState(1);
  const [isUpdateVdaStatusLoading, setIsUpdateVdaStatusLoading] =
    useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const emailId =
    userDetailsList.filter((item) => item.name === 'Email:')[0].value || '';

  const phoneNumber =
    userDetailsList.filter((item) => item.name === 'Mobile number:')[0].value ||
    ' ';
  const username =
    userDetailsList.filter((item) => item.name === 'Username:')[0].value || '';

  const kycName =
    userDetailsList.filter((item) => item.name === 'Kyc Name:')[0].value || '';

  const dateOfBirthValue = userDetailsList.find((item) => item.name === 'Date of birth:')?.value;
  const dateOfBirth = formatDobDate(dateOfBirthValue) || 'Not provided';

  const dispatch = useDispatch();
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleUpdateVdaStatusSubmit = ({ value }) => {
    if (!value) {
      dispatch(
        setAlert({
          title: 'Please enter a valid status',
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
      return;
    }
    if (value === userData?.vdaEligibilityStatus) {
      return;
    }

    setIsUpdateVdaStatusLoading(true);
    dispatch(
      updateVdaEligibilityStatus({
        userId: userData?.userId,
        newStatus: value,
        onSuccess: () => {
          handleUpdateVdaStatusClose();
          dispatch(getUserById(userData?.userId));
        },
        onComplete: () => {
          setIsUpdateVdaStatusLoading(false);
        }
      })
    );
  };
  const handleUserDetailsUpdate = ({ fieldName, fieldValue }) => {
    const isValidUpdate = Object.values(SUPPORTED_USER_DETAILS_UPDATE_FIELDS).includes(fieldName);

    if (isValidUpdate) {
      dispatch(
        updateUserDetails({
          userId: userData?.userId,
          fieldName,
          fieldValue,
          onSuccess: () => {
            dispatch(getUserById(userData?.userId));
          }
        })
      );
    } else {
      dispatch(
        setAlert({
          title: 'Invalid Update Attempted',
          status: ALERT_STATUS_TYPE.ERROR
        })
      );
    }
  };


  return (
    <Box
      position="relative"
      maxW={{ base: '100%', xl: '100%' }}
      w={{ base: '100%', xl: 'auto' }}
    >
      <UserDetailsUpdateModal
        title="Kyc Name Update"
        isOpen={isKycUpdateOpen}
        onClose={onKycUpdateClose}
        initialValue={kycName}
        fieldName="kycName"
        isLoading={isModalLoading}
        onSubmit={handleUserDetailsUpdate}
      />

      <UserDetailsUpdateModal
        title="Username Update"
        isOpen={isUsernameUpdateOpen}
        onClose={onUsernameUpdateClose}
        initialValue={username}
        fieldName="name"
        isLoading={isModalLoading}
        onSubmit={handleUserDetailsUpdate}
      />

      <UserDetailsUpdateModal
        title="Email Update"
        isOpen={isEmailUpdateOpen}
        onClose={onEmailUpdateClose}
        initialValue={emailId}
        fieldName="email"
        isLoading={isModalLoading}
        onSubmit={handleUserDetailsUpdate}
      />

      <UserDetailsUpdateModal
        title="Phone Number Update"
        isOpen={isPhoneUpdateOpen}
        onClose={onPhoneUpdateClose}
        initialValue={phoneNumber}
        fieldName="phoneNumber"
        isLoading={isModalLoading}
        onSubmit={handleUserDetailsUpdate}
      />

      <UserDetailsUpdateModal
        title="Date of Birth Update (yyyy-mm-dd)"
        isOpen={isDobUpdateOpen}
        onClose={onDobUpdateClose}
        initialValue={dateOfBirth}
        fieldName="dob"
        isLoading={isModalLoading}
        onSubmit={handleUserDetailsUpdate}
      />
      <ModalLayout
        isOpen={isFreeTransferModalOpen}
        onClose={onFreeTransferModalClose}
      >
        <AddFreeTransfers
          userId={userData?.userId}
          onClose={onFreeTransferModalClose}
          creditorsList={userData?.creditors}
        />
      </ModalLayout>
      <Box
        bg="backGround"
        w={{ base: 'auto', xl: '100%' }}
        className="scroll"
        justifyContent="stretch"
        border="1px solid"
        borderColor="whiteAlpha.300"
        borderRadius="xl"
      >
        <VStack p={4} gap={4} className="scroll-sm" position={'relative'}>
          <HStack style={{ alignSelf: 'start' }} gap={4}>
            <Image
              src={profileLogo}
              alt="Profile Logo"
              width={'4rem'}
              height={'4rem'}
            />
            <VStack alignItems={'start'} gap={0}>
              <Heading fontWeight="400" color="white" size={'lg'}>
                {userData?.name}
              </Heading>
              {referralCode && (
                <Tooltip
                  hasArrow
                  label={'Referral Code'}
                  bg={'shadedGray.800'}
                  px={2}
                  py={1}
                >
                  <HStack bg={'altGray.300'} color={'brand.200'}>
                    <BsPeopleFill />
                    <CopyableText title="Referral Code">
                      {referralCode}
                    </CopyableText>
                  </HStack>
                </Tooltip>
              )}
            </VStack>
          </HStack>
          {userData && (
            <>
              <Grid
                gridTemplateColumns={{ base: '1fr', sm: '1fr 1fr' }}
                rowGap={2}
                w="auto"
                alignSelf="start"
                alignItems="stretch"
                justifyContent="space-between"
                borderRadius="xl"
                py={2}
              >
                {userDetailsList.map((item, idx) => {
                  return (
                    <React.Fragment key={item.name}>
                      <Box
                        color={'gray'}
                        pt="0"
                        pl={'0'}
                        pr={0}
                        border="none"
                        maxWidth={'20rem'}
                        whiteSpace={'break-word'}
                      >
                        {item.name}
                      </Box>
                      <Box color={'white'} pt="0" border="none">
                        {item.name === 'App Platform:' &&
                          item.value &&
                          activeDeviceDetails ? (
                          <ActiveDevice
                            appPlatform={item.value}
                            activeDeviceDetails={activeDeviceDetails}
                          />
                        ) : item.name === 'Free transfers left:' &&
                          allowAddFreeTransfer ? (
                          <Button
                            variant={'link'}
                            color={'white'}
                            fontWeight={400}
                            padding={0}
                            textDecoration={'none !important'}
                            rightIcon={<AiOutlinePlusCircle />}
                            onClick={onFreeTransferModalOpen}
                          >
                            {item.value}
                          </Button>
                        ) : item.name === 'User Type:' &&
                          item.value === 'GOLDEN_TIER_USER' ? (
                          <Link
                            as={ReactLink}
                            to={`/user/${userData?.userId}/golden-ticket`}
                            color="white"
                            justifyContent={'flex-start'}
                            w={'100%'}
                            fontSize={'sm'}
                          >
                            <HStack>
                              <Text>{item.value}</Text>
                              <GiTicket display={'inline'} />
                            </HStack>
                          </Link>
                        ) : item.name === 'Vance Coins:' &&
                          item.value !== '-' ? (
                          <HStack alignItems={'center'}>
                            <Button
                              variant={'link'}
                              color={'white'}
                              fontWeight={400}
                              padding={0}
                              textDecoration={'none !important'}
                              rightIcon={<BiSync />}
                              onClick={onWalletSync}
                              isLoading={isSyncingWallet}
                            >
                              {item.value}
                            </Button>
                            <Link
                              as={ReactLink}
                              to={`/user/${userData?.userId}/redeemed-coins`}
                              color="white"
                              _hover={{
                                textDecor: 'underline'
                              }}
                              fontSize={'sm'}
                            >
                              <BsCashCoin />
                            </Link>
                          </HStack>
                        ) : item.isCopyable ? (
                          <HStack>
                            <CopyableText title={item.name.replace(':', '')}>
                              {item.value}
                            </CopyableText>
                            {item.isEditable &&
                              hasAnyAuthority(
                                ROLES.ADMIN_ROLE,
                                ROLES.MANAGER_ROLE,
                                ROLES.USER_DETAILS_MANAGER
                              ) && (
                                <Button
                                  rightIcon={<MdEdit />}
                                  onClick={() => {
                                    item.name === 'Email:' &&
                                      onEmailUpdateOpen();
                                    item.name === 'Mobile number:' &&
                                      onPhoneUpdateOpen();
                                    item.name === 'Username:' &&
                                      onUsernameUpdateOpen();
                                    item.name === 'Kyc Name:' &&
                                      onKycUpdateOpen();
                                    item.name === 'Date of birth:' &&
                                      onDobUpdateOpen();
                                  }}
                                  variant={'link'}
                                />
                              )}
                          </HStack>
                        ) : (
                          <HStack
                            alignItems={'center'}
                            justifyContent={'start'}
                            gap={1}
                          >
                            {item.isArray ? (
                              item.value?.length > 0 ? (
                                <Button
                                  variant="link"
                                  color="brand.200"
                                  fontWeight={400}
                                  padding={0}
                                  _hover={{ textDecoration: 'none' }}
                                  onClick={onFeaturesModalOpen}
                                  fontSize="md"
                                >
                                  View Features
                                </Button>
                              ) : (
                                <Text color="gray.400">No features whitelisted</Text>
                              )
                            ) : (
                              <Text>{item.value}</Text>
                            )}
                            {item.name === 'Vance Cash:' &&
                              item.value !== '-' && (
                                <Link
                                  as={ReactLink}
                                  to={`/user/${userData?.userId}/vance-cash/${versionNumber}`}
                                  color="white"
                                  _hover={{
                                    textDecor: 'underline'
                                  }}
                                  fontSize={'sm'}
                                >
                                  <FaArrowRightFromBracket />
                                </Link>
                              )}
                            {item.name === 'Vance Cash:' &&
                              item.value !== '-' && (
                                <IconButton
                                  variant={'unstyled'}
                                  icon={<BiSync />}
                                  onClick={() => {
                                    onVanceCashWalletSync(versionNumber);
                                  }}
                                  isLoading={isSyncingWallet}
                                />
                              )}
                            {item.name === 'Aspora Cash:' &&
                              item.value !== '-' && (
                                <Link
                                  as={ReactLink}
                                  to={`/user/${userData?.userId}/${`CASH`}/2`}
                                  color="white"
                                  _hover={{
                                    textDecor: 'underline'
                                  }}
                                  fontSize={'sm'}
                                >
                                  <FaArrowRightFromBracket />
                                </Link>
                              )}
                            {item.name === 'Aspora Coins:' &&
                              item.value !== '-' && (
                                <Link
                                  as={ReactLink}
                                  to={`/user/${userData?.userId}/${`COINS`}/2`}
                                  color="white"
                                  _hover={{
                                    textDecor: 'underline'
                                  }}
                                  fontSize={'sm'}
                                >
                                  <FaArrowRightFromBracket />
                                </Link>
                              )}
                            {item?.property === 'vda-eligibility-status' && (
                              <Button
                                rightIcon={<MdEdit />}
                                onClick={() => {
                                  onUpdateVdaStatusOpen();
                                }}
                                variant={'link'}
                              />
                            )}
                            {
                              item?.name === 'User Profile: ' &&  (
                                <Link
                                  as={ReactLink}
                                  to={`/user/${userData?.userId}/${`user-profile`}`}
                                  color="white"
                                  _hover={{
                                    textDecor: 'underline'
                                  }}
                                  fontSize={'sm'}
                                >
                                  <FaArrowRightFromBracket />
                                </Link>
                              )
                            }
                          </HStack>
                        )}
                      </Box>
                    </React.Fragment>
                  );
                })}
              </Grid>
              <Grid
                templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }}
                w={'100%'}
                gap={{ base: 4, xl: 2 }}
                justifyContent={{ base: 'space-between', lg: 'flex-start' }}
              >
                <Link as={ReactLink} to={`/user/${userData?.userId}/workflows`}>
                  <Button
                    colorScheme={'brand'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    w={'100%'}
                    size={'sm'}
                    bg={'#333333'}
                    className="scroll"
                    border={'2px solid'}
                    borderColor={'whiteAlpha.300'}
                    borderRadius={'xl'}
                    p={4}
                    gap={2}
                  >
                    <TbGitBranch display={'inline'} />
                    <Text fontSize={'xs'}>Workflows</Text>
                  </Button>
                </Link>
                <Link as={ReactLink} to={`/user/${userData.userId}/documents`}>
                  <Button
                    colorScheme={'brand'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    w={'100%'}
                    size={'sm'}
                    bg={'#333333'}
                    className="scroll"
                    border={'2px solid'}
                    borderColor={'whiteAlpha.300'}
                    borderRadius={'xl'}
                    p={4}
                    gap={2}
                  >
                    <HiOutlineDocumentText display={'inline'} />
                    <Text fontSize={'xs'}>Documents</Text>
                  </Button>
                </Link>
                <Link as={ReactLink} to={`/user/${userData?.userId}/referrals`} gridColumn={{ base: 'span 1', lg: 'span 2' }}>
                  <Button
                    colorScheme={'brand'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    w={'100%'}
                    size={'sm'}
                    bg={'#333333'}
                    className="scroll"
                    border={'2px solid'}
                    borderColor={'whiteAlpha.300'}
                    borderRadius={'xl'}
                    p={4}
                    gap={2}
                  >
                    <BiAward display={'inline'} />
                    <Text fontSize={'xs'}>Referrals</Text>
                  </Button>
                </Link>
              </Grid>
            </>
          )}
        </VStack>
      </Box>

      {/* Features Modal */}
      <ModalLayout isOpen={isFeaturesModalOpen} onClose={onFeaturesModalClose}>
        <RawDataViewer 
          title="Features Whitelisted" 
          data={userData?.featuresWhitelisted || []} 
        />
      </ModalLayout>
    </Box>
  );
};

export default UserDetails;
